import { FileCardV2, getFileType, Spacer, Text } from "@vericus/cadmus-ui";

/**
 * Represents a file that is currently being uploaded
 */
export interface UploadingFile {
  /** Temporary unique identifier for the uploading file */
  tempId: string;
  /** Name of the file being uploaded */
  name: string;
  /** Upload progress percentage (0-100) */
  progress: number;
  /** The File object being uploaded */
  file: File;
  /** Whether the upload has failed */
  hasError?: boolean;
  /** Error message to display when upload fails */
  errorMessage?: string;
}

/**
 * Props for the UploadingFilesList component
 */
interface UploadingFilesListProps {
  /** Array of files currently being uploaded */
  files: UploadingFile[];
  /** Callback to retry a failed upload */
  onRetry?: (tempId: string) => void;
  /** Callback to dismiss/remove a failed upload */
  onDismiss?: (tempId: string) => void;
}

/**
 * UploadingFilesList component
 *
 * Displays a list of files that are currently being uploaded with
 * their progress indicators. Shows a header with the count of uploading files.
 * Uses FileCardV2's uploading state with progress bar.
 *
 * @example
 * ```tsx
 * <UploadingFilesList
 *   files={[
 *     { tempId: '123', name: 'document.pdf', progress: 45, file: fileObject }
 *   ]}
 * />
 * ```
 */
export function UploadingFilesList({
  files,
  onRetry,
  onDismiss,
}: UploadingFilesListProps) {
  if (files.length === 0) {
    return null;
  }

  return (
    <>
      <Spacer spacing={24} />
      <div>
        <Text kind="label">Uploading ({files.length})</Text>
        <Spacer spacing={12} />
        {files.map((file) => {
          const fileType = getFileType(file.name);
          const fileExtension = file.name.split(".").pop()?.toLowerCase();
          const fileSizeKB = file.file.size
            ? `${(file.file.size / 1024).toFixed(0)} KB`
            : undefined;

          return (
            <FileCardV2
              key={file.tempId}
              fileInfo={{
                name: file.name,
                type: fileType,
                size: fileSizeKB,
                extension: fileExtension?.toUpperCase(),
              }}
              isUploading={!file.hasError}
              uploadProgress={file.progress}
              hasError={file.hasError}
              errorMessage={file.errorMessage}
              onRetry={
                file.hasError && onRetry
                  ? () => onRetry(file.tempId)
                  : undefined
              }
              onDelete={
                file.hasError && onDismiss
                  ? () => onDismiss(file.tempId)
                  : undefined
              }
            />
          );
        })}
      </div>
    </>
  );
}
